import { z } from 'zod';

/**
 * Coordinator-tunable autofill settings (SPEC §4.3), stored per event season.
 * Shift length is per post type because a car park and a course post are not
 * staffed the same way; everything else is a whole-day rule.
 */

const lengthsSchema = z
  .object({
    /** Length autofill aims for when it cuts a long window into shifts. */
    targetMin: z.number().int().min(15).max(720),
    /** Hard ceiling for one generated shift. */
    maxMin: z.number().int().min(15).max(720),
  })
  .refine((v) => v.maxMin >= v.targetMin, {
    message: 'Najdaljša izmena ne sme biti krajša od ciljne.',
  });

export const shiftPlanningSchema = z
  .object({
    byType: z.object({
      trasa: lengthsSchema,
      'parkirišče': lengthsSchema,
      'štartno-ciljni': lengthsSchema,
    }),
    /** Never cut a piece shorter than this; a naturally short window is left whole. */
    minPieceMin: z.number().int().min(5).max(240).default(45),
    /**
     * The day autofill aims to give one person — a total across all their
     * shifts, not the length of one. It fills everybody to this before it
     * gives anyone a longer day, which is what spreads work across the pool.
     * Set it equal to `maxDailyMin` to pack people up instead.
     */
    softDailyMin: z.number().int().min(60).max(1440).default(330),
    /** Most minutes autofill will give one person in a day, whatever it takes. */
    maxDailyMin: z.number().int().min(60).max(1440).default(480),
    /** How far a cut may move to land on a handover or an availability edge. */
    alignToleranceMin: z.number().int().min(0).max(240).default(90),
  })
  .refine((v) => v.softDailyMin <= v.maxDailyMin, {
    message: 'Priporočeni dan ne sme biti daljši od največjega.',
    path: ['softDailyMin'],
  })
  /*
   * The floor and the ceiling have to leave no gap. `n` pieces of a window can
   * only be legal when its length lands inside [n·minPieceMin, n·maxMin], and
   * those ranges only touch for every n once maxMin reaches twice the floor.
   * Below that a window can fall between two of them — 700 min at a 240 floor
   * and a 330 ceiling is three pieces of 233 (under the floor) or two of 350
   * (over the ceiling) — and the split would have to break one of its own
   * promises.
   */
  .refine((v) => Object.values(v.byType).every((l) => l.maxMin >= 2 * v.minPieceMin), {
    message: 'Najdaljša izmena mora biti vsaj dvakrat daljša od najkrajšega odseka.',
    path: ['minPieceMin'],
  });

export type ShiftPlanning = z.infer<typeof shiftPlanningSchema>;

/**
 * Defaults read off the 2025 roster: a 4 h shift was the median across every
 * post type, and nothing ran longer than 5 h 30 — which is also the day the
 * pool is big enough to give almost everyone.
 */
export const DEFAULT_SHIFT_PLANNING: ShiftPlanning = {
  byType: {
    trasa: { targetMin: 240, maxMin: 330 },
    'parkirišče': { targetMin: 240, maxMin: 330 },
    'štartno-ciljni': { targetMin: 240, maxMin: 330 },
  },
  minPieceMin: 45,
  softDailyMin: 330,
  maxDailyMin: 480,
  alignToleranceMin: 90,
};

/**
 * Applying a plan (SPEC §4.3). One request carries the whole accepted plan so
 * it lands in a single transaction: partial application would leave a roster
 * half-built and a pile of version bumps behind it.
 */
export const autofillApplySchema = z.object({
  eventDayId: z.uuid(),
  /** Silent edit: fill without resetting anyone's confirmation. */
  silent: z.boolean().default(false),
  assignments: z
    .array(
      z.object({
        postId: z.uuid(),
        volunteerId: z.uuid(),
        fromMin: z.number().int().min(0).max(1440),
        toMin: z.number().int().min(0).max(1440),
        /** Staff this open slot, or create a new shift when null. */
        shiftId: z.uuid().nullable().default(null),
      }),
    )
    .min(1)
    .max(500),
});

export type AutofillApply = z.infer<typeof autofillApplySchema>;
